import { startCase } from "lodash";
import type { Color, PDFPage } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";

import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import {
  clamp,
  createField,
  type FieldType,
  type LayoutField,
  type SignerSlot,
} from "./SignatureRequestPdfLayoutEditor.constants";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

export type SignerTemplateMarker = {
  fontFamily?: string;
  fontSize?: number;
  height: number;
  page: number;
  pageHeight: number;
  underline?: {
    color: string;
    thickness: number;
    x: number;
    y: number;
    width: number;
  };
  variable: string;
  width: number;
  x: number;
  y: number;
};

// Cover the placeholder glyphs without blanking neighboring document content.
export function getSignerTemplateMarkerMask(
  marker: SignerTemplateMarker,
  pageWidth: number,
  pageHeight: number,
) {
  const horizontalPadding = 1;
  const topPadding = 0.5;
  const bottomPadding = 4.5;

  return {
    height: marker.height * pageHeight + topPadding + bottomPadding,
    width: marker.width * pageWidth + horizontalPadding * 2,
    x: marker.x * pageWidth - horizontalPadding,
    y: pageHeight - (marker.y + marker.height) * pageHeight - bottomPadding,
  };
}

function findMarkerUnderlines(
  markers: SignerTemplateMarker[],
  operatorList: Awaited<ReturnType<pdfjsLib.PDFPageProxy["getOperatorList"]>>,
  pageWidth: number,
  pageHeight: number,
) {
  type GraphicsState = {
    fillColor: string;
    lineWidth: number;
    strokeColor: string;
    transform: number[];
  };
  let state: GraphicsState = {
    fillColor: "#000000",
    lineWidth: 0.6,
    strokeColor: "#000000",
    transform: [1, 0, 0, 1, 0, 0],
  };
  const stateStack: GraphicsState[] = [];

  operatorList.fnArray.forEach((operator, index) => {
    const args = operatorList.argsArray[index];
    if (operator === pdfjsLib.OPS.save) {
      stateStack.push({ ...state, transform: [...state.transform] });
      return;
    }
    if (operator === pdfjsLib.OPS.restore) {
      state = stateStack.pop() ?? state;
      return;
    }
    if (operator === pdfjsLib.OPS.transform) {
      state.transform = pdfjsLib.Util.transform(
        state.transform,
        args as number[],
      );
      return;
    }
    if (
      operator === pdfjsLib.OPS.setStrokeRGBColor &&
      typeof args[0] === "string"
    ) {
      state.strokeColor = args[0];
      return;
    }
    if (
      operator === pdfjsLib.OPS.setFillRGBColor &&
      typeof args[0] === "string"
    ) {
      state.fillColor = args[0];
      return;
    }
    if (operator === pdfjsLib.OPS.setLineWidth && typeof args[0] === "number") {
      state.lineWidth = args[0];
      return;
    }
    if (operator !== pdfjsLib.OPS.constructPath) return;

    const paintOperator = args[0];
    if (
      paintOperator !== pdfjsLib.OPS.stroke &&
      paintOperator !== pdfjsLib.OPS.fill
    ) {
      return;
    }
    const bounds = args[2] as ArrayLike<number> | undefined;
    if (!bounds || bounds.length !== 4) return;
    const transformPoint = (pointX: number, pointY: number) => [
      state.transform[0] * pointX +
        state.transform[2] * pointY +
        state.transform[4],
      state.transform[1] * pointX +
        state.transform[3] * pointY +
        state.transform[5],
    ];
    const start = transformPoint(bounds[0], bounds[1]);
    const end = transformPoint(bounds[2], bounds[3]);
    const x = Math.min(start[0], end[0]);
    const y = Math.min(start[1], end[1]);
    const width = Math.abs(end[0] - start[0]);
    const thickness =
      paintOperator === pdfjsLib.OPS.stroke
        ? state.lineWidth
        : Math.abs(end[1] - start[1]);
    if (thickness > 1.5 || width < 8) return;

    markers.forEach((marker) => {
      const markerX = marker.x * pageWidth;
      const markerWidth = marker.width * pageWidth;
      const baseline = pageHeight * (1 - marker.y - marker.height);
      const overlap =
        Math.min(x + width, markerX + markerWidth) - Math.max(x, markerX);
      if (
        overlap < markerWidth * 0.8 ||
        baseline - y < 0.5 ||
        baseline - y > 4
      ) {
        return;
      }

      marker.underline = {
        color:
          paintOperator === pdfjsLib.OPS.stroke
            ? state.strokeColor
            : state.fillColor,
        thickness: Math.max(thickness, 0.6),
        x,
        y,
        width,
      };
    });
  });
}

export function restoreSignerTemplateMarkerUnderline(
  page: PDFPage,
  marker: SignerTemplateMarker,
  rgb: (red: number, green: number, blue: number) => Color,
) {
  const details = getSignerTemplateMarkerDetails(marker.variable);
  if (!marker.underline && !details?.underline) return;

  if (!marker.underline) {
    const { width: pageWidth, height: pageHeight } = page.getSize();
    const x = marker.x * pageWidth;
    const y = pageHeight * (1 - marker.y - marker.height) - 2;
    const width = (details?.sizeOverride?.width ?? marker.width) * pageWidth;
    page.drawLine({
      color: rgb(0, 0, 0),
      end: { x: Math.min(x + width, pageWidth), y },
      start: { x, y },
      thickness: 0.6,
    });
    return;
  }

  const { color, thickness, width, x, y } = marker.underline;
  if (!/^#[\da-f]{6}$/i.test(color)) return;
  page.drawLine({
    color: rgb(
      Number.parseInt(color.slice(1, 3), 16) / 255,
      Number.parseInt(color.slice(3, 5), 16) / 255,
      Number.parseInt(color.slice(5, 7), 16) / 255,
    ),
    end: { x: x + width, y },
    start: { x, y },
    thickness,
  });
}

type PositionedText = {
  runs?: { start: number; fontFamily?: string; fontSize: number }[];
  fontFamily?: string;
  height: number;
  page: number;
  pageHeight: number;
  text: string;
  width: number;
  x: number;
  y: number;
};

const SIGNER_VARIABLE_PATTERN =
  /\{\{((?:User)?Signer)(\d*)_([A-Za-z]+)(?:_([^{}]+))?\}\}/g;

const FORMATTING_SUFFIX_PATTERN =
  /_(?:upperCase|lowerCase|titleCase|plural|possessive|short|long)$/;

function findMarkersInText(text: PositionedText): SignerTemplateMarker[] {
  return Array.from(text.text.matchAll(SIGNER_VARIABLE_PATTERN)).map(
    (match) => {
      const markerStyle = text.runs
        ?.filter((run) => run.start <= match.index)
        .at(-1);
      let widthRatio = match[0].length / text.text.length;
      let offsetRatio = match.index / text.text.length;

      // A PDF text item can contain both the placeholder and surrounding words.
      // Character counts overestimate the mask when those glyphs have different
      // widths, so measure the substrings with the extracted font when possible.
      if (text.fontFamily && typeof document !== "undefined") {
        const context = document.createElement("canvas").getContext("2d");
        if (context) {
          context.font = `100px ${text.fontFamily}`;
          const fullWidth = context.measureText(text.text).width;
          if (fullWidth > 0) {
            offsetRatio =
              context.measureText(text.text.slice(0, match.index)).width /
              fullWidth;
            widthRatio = context.measureText(match[0]).width / fullWidth;
          }
        }
      }

      return {
        fontFamily: markerStyle?.fontFamily ?? text.fontFamily,
        fontSize: markerStyle?.fontSize ?? text.height * text.pageHeight,
        height: text.height,
        page: text.page,
        pageHeight: text.pageHeight,
        variable: match[0],
        width: text.width * widthRatio,
        x: text.x + text.width * offsetRatio,
        y: text.y,
      };
    },
  );
}

function combineNearbyTextItems(items: PositionedText[]) {
  const sortedItems = [...items].sort(
    (left, right) =>
      left.page - right.page || left.y - right.y || left.x - right.x,
  );
  const combined: PositionedText[] = [];

  for (const item of sortedItems) {
    const previous = combined.at(-1);
    const previousRight = previous ? previous.x + previous.width : 0;
    const isSameLine =
      previous?.page === item.page &&
      Math.abs(previous.y - item.y) <= Math.max(previous.height, item.height);
    const isNearby = item.x - previousRight <= 0.02;

    if (previous && isSameLine && isNearby) {
      const right = Math.max(previousRight, item.x + item.width);
      if (!previous.text.includes("{{") && item.text.includes("{{")) {
        previous.fontFamily = item.fontFamily;
      }
      previous.runs = [
        ...(previous.runs ?? []),
        ...(
          item.runs ?? [
            {
              start: 0,
              fontFamily: item.fontFamily,
              fontSize: item.height * item.pageHeight,
            },
          ]
        ).map((run) => ({ ...run, start: run.start + previous.text.length })),
      ];
      previous.text += item.text;
      previous.height = Math.max(previous.height, item.height);
      previous.width = right - previous.x;
      previous.y = Math.min(previous.y, item.y);
      continue;
    }

    combined.push({
      ...item,
      runs: item.runs ?? [
        {
          start: 0,
          fontFamily: item.fontFamily,
          fontSize: item.height * item.pageHeight,
        },
      ],
    });
  }

  return combined;
}

export async function extractSignerTemplateMarkersFromPdf(
  pdfBytes: ArrayBuffer,
): Promise<SignerTemplateMarker[]> {
  const loadingTask = pdfjsLib.getDocument({ data: pdfBytes.slice(0) });
  const document = await loadingTask.promise;
  const textItems: PositionedText[] = [];

  try {
    for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
      const page = await document.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      const content = await page.getTextContent();
      const styles: Partial<typeof content.styles> = content.styles;

      content.items.forEach((item) => {
        if (!("str" in item) || !item.str) return;

        const transform = pdfjsLib.Util.transform(
          viewport.transform,
          item.transform,
        ) as [number, number, number, number, number, number];
        const height = Math.hypot(transform[2], transform[3]) / viewport.height;

        textItems.push({
          fontFamily: styles[item.fontName]?.fontFamily,
          height,
          page: pageNumber,
          pageHeight: viewport.height,
          text: item.str,
          width: (item.width * viewport.scale) / viewport.width,
          x: transform[4] / viewport.width,
          y: transform[5] / viewport.height - height,
        });
      });
    }

    const directMarkers = textItems.flatMap(findMarkersInText);
    const incompleteTextItems = textItems.filter(
      (item) => findMarkersInText(item).length === 0,
    );
    const combinedMarkers =
      combineNearbyTextItems(incompleteTextItems).flatMap(findMarkersInText);
    const uniqueMarkers = new Map<string, SignerTemplateMarker>();

    [...directMarkers, ...combinedMarkers].forEach((marker) => {
      const key = [
        marker.page,
        marker.variable,
        marker.x.toFixed(4),
        marker.y.toFixed(4),
      ].join(":");
      uniqueMarkers.set(key, marker);
    });

    const markers = [...uniqueMarkers.values()];
    for (const pageNumber of new Set(markers.map((marker) => marker.page))) {
      const page = await document.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      try {
        findMarkerUnderlines(
          markers.filter((marker) => marker.page === pageNumber),
          await page.getOperatorList(),
          viewport.width,
          viewport.height,
        );
      } catch (error) {
        console.warn("Could not inspect signer placeholder underlines", error);
      }
    }

    return markers;
  } finally {
    await loadingTask.destroy();
  }
}

export async function maskSignerTemplateMarkersInPdf(
  pdfBytes: ArrayBuffer,
): Promise<Blob | null> {
  const markers = await extractSignerTemplateMarkersFromPdf(pdfBytes);
  if (markers.length === 0) return null;

  const { PDFDocument, rgb } = await import("pdf-lib");
  const pdfDocument = await PDFDocument.load(pdfBytes);

  markers.forEach((marker) => {
    const page = pdfDocument.getPage(marker.page - 1);
    const { width: pageWidth, height: pageHeight } = page.getSize();
    page.drawRectangle({
      color: rgb(1, 1, 1),
      ...getSignerTemplateMarkerMask(marker, pageWidth, pageHeight),
    });
    restoreSignerTemplateMarkerUnderline(page, marker, rgb);
  });

  const bytes = await pdfDocument.save();
  return new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
}

export function getSignerTemplateMarkerDetails(variable: string): {
  fieldLabel: string;
  fieldType: FieldType;
  signerIndex: number;
  signerKind: SignerSlot["kind"];
  sizeOverride?: Pick<LayoutField, "height" | "width">;
  underline: boolean;
} | null {
  const match = variable.match(
    /^\{\{((?:User)?Signer)(\d*)_([A-Za-z]+)(?:_([^{}]+))?\}\}$/,
  );
  if (!match) return null;

  const [, prefix, rawIndex, rawType, rawLabel = ""] = match;
  const normalizedType = rawType.toLowerCase();
  let normalizedLabel = rawLabel;
  let underline = false;
  let sizeOverride: Pick<LayoutField, "height" | "width"> | undefined;
  let previousLabel: string;
  do {
    previousLabel = normalizedLabel;
    const underlineSuffix = normalizedLabel.match(/(?:^|_)underline$/i);
    if (underlineSuffix) {
      underline = true;
      normalizedLabel = normalizedLabel.slice(0, -underlineSuffix[0].length);
    }
    const sizeSuffix = normalizedLabel.match(/(?:^|_)w(\d{1,3})h(\d{1,3})$/i);
    if (sizeSuffix) {
      const width = Number(sizeSuffix[1]);
      const height = Number(sizeSuffix[2]);
      if (width >= 1 && width <= 100 && height >= 1 && height <= 100) {
        sizeOverride = { width: width / 100, height: height / 100 };
        normalizedLabel = normalizedLabel.slice(0, -sizeSuffix[0].length);
      }
    }
    normalizedLabel = normalizedLabel.replace(FORMATTING_SUFFIX_PATTERN, "");
  } while (normalizedLabel !== previousLabel);
  const isInitials =
    normalizedType === "initials" ||
    (normalizedType === "text" && normalizedLabel.toLowerCase() === "initials");
  const fieldType: FieldType =
    normalizedType === "signature"
      ? "signature"
      : isInitials
        ? "initials"
        : normalizedType.startsWith("date")
          ? "date"
          : "text";
  const isSigningDate =
    normalizedType === "date" &&
    (!normalizedLabel || /^(date|signingDate)$/i.test(normalizedLabel));
  const fallbackLabel = fieldType === "text" ? "Text" : startCase(fieldType);

  return {
    fieldLabel: isSigningDate
      ? "Signing Date"
      : normalizedLabel
        ? startCase(normalizedLabel)
        : fallbackLabel,
    fieldType,
    signerIndex: Math.max(Number(rawIndex || "1") - 1, 0),
    signerKind: prefix === "UserSigner" ? "user" : "contact",
    sizeOverride,
    underline,
  };
}

export function findSignerTemplateMarkerForField(
  field: LayoutField,
  markers: SignerTemplateMarker[],
) {
  const matches = markers.filter((marker) => {
    const details = getSignerTemplateMarkerDetails(marker.variable);
    const labelMatches =
      details?.fieldLabel === field.label ||
      (field.type === "date" &&
        field.label === "Date" &&
        details?.fieldLabel === "Signing Date");
    return (
      marker.page === field.page &&
      details?.fieldType === field.type &&
      labelMatches &&
      Math.abs(marker.x - field.x) < 0.003 &&
      Math.abs(marker.y + marker.height - field.y - field.height) < 0.003
    );
  });
  return matches.length === 1 ? matches[0] : undefined;
}

export function convertSignerTemplateMarkersToFields(
  markers: SignerTemplateMarker[],
  signers: SignerSlot[],
): LayoutField[] {
  return markers.flatMap((marker) => {
    const details = getSignerTemplateMarkerDetails(marker.variable);
    if (!details) return [];

    const signerPrefix =
      details.signerKind === "user" ? "UserSigner" : "Signer";
    const signer = signers.find(
      (candidate) =>
        candidate.kind === details.signerKind &&
        candidate.label === `${signerPrefix}${details.signerIndex + 1}`,
    );
    if (!signer) return [];

    const field = createField(
      details.fieldType,
      signer.id,
      signer.signerOrder,
      marker.page,
      undefined,
      undefined,
      details.sizeOverride,
    );

    // Use the first opening brace's estimated left edge and text baseline as
    // the field's bottom-left anchor, independent of its height.
    const y = marker.y + marker.height - field.height;

    return [
      {
        ...field,
        ...(field.type === "text" || field.type === "date"
          ? getSignerTemplateMarkerTextStyle(marker)
          : {}),
        label: details.fieldLabel,
        x: clamp(marker.x, 0, 1 - field.width),
        y: clamp(y, 0, 1 - field.height),
      },
    ];
  });
}

export function getSignerTemplateMarkerTextStyle(marker: SignerTemplateMarker) {
  const fontFamily: LayoutField["fontFamily"] = /mono|courier/i.test(
    marker.fontFamily ?? "",
  )
    ? "Courier New"
    : /sans|arial|helvetica|calibri/i.test(marker.fontFamily ?? "")
      ? "Arial"
      : "Times New Roman";
  return {
    fontFamily,
    fontSize:
      typeof marker.fontSize === "number" && Number.isFinite(marker.fontSize)
        ? clamp(marker.fontSize, 6, 72)
        : 11,
  };
}
